import { useMemo } from 'react';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { useDemoUsers } from '@/hooks/common/useDemoUsers';
import { useSelectedOrgId } from '@/hooks/common/useSelectedOrgId';
import { useDealOperations } from '@/hooks/deals/useDealOperations';
import {
  buildContacts,
  buildContactsFromParticipants,
  buildConversations,
  type Contact,
  type Conversation,
} from '@/views/people/lib/data';

export function usePeopleData() {
  const isDemo = useIsDemo();
  const { profilePictures, userIds } = useDemoUsers();
  const selectedOrgId = useSelectedOrgId();
  // In prod, reuse the same deal list the kanban/vault already fetch
  // (useDealPipelineData reads from this same hook) so contacts come from
  // real counterparties without a second round-trip. Demo passes undefined
  // so this SWR key stays null and the demo path below owns the data.
  const { deals } = useDealOperations(isDemo ? undefined : selectedOrgId);

  const contacts: Contact[] = useMemo(() => {
    if (isDemo) {
      return buildContacts(profilePictures, userIds, selectedOrgId);
    }
    // Aggregate every DealParticipant the deal list carried, deduped by
    // identifier inside buildContactsFromParticipants. Empty until the user
    // has a deal with at least one identified counterparty — the People page
    // renders its "No contacts yet" empty state in that case.
    const participants = deals.flatMap((deal) => deal.participants ?? []);
    return buildContactsFromParticipants(participants);
  }, [isDemo, profilePictures, selectedOrgId, userIds, deals]);

  const conversations: Conversation[] = useMemo(
    () => (isDemo ? buildConversations(profilePictures, userIds, selectedOrgId) : []),
    [isDemo, profilePictures, selectedOrgId, userIds],
  );

  return { contacts, conversations };
}
